import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { FaChevronDown, FaChevronUp, FaInbox, FaTimes } from "react-icons/fa";
import Loader from "../Loader/Loader";
import InvoiceFollowupButton from "../GenericComponents/InvoiceFollowupButton";
import InvoiceFollowupModal from "../GenericComponents/InvoiceFollowupModal";
import InvoiceFollowupsTabPanel from "../GenericComponents/InvoiceFollowupsTabPanel";
import { hasInvoiceFollowupPermission } from "../../utilis/followupConstants";
import {
  applyInvoiceCatalog,
  fetchInvoiceCatalog,
} from "../../utilis/invoiceWorkspaceApi";

function formatHistoryDate(value) {
  if (!value) return "—";

  const formats = [
    moment.ISO_8601,
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DDTHH:mm:ss.SSSSSSZ",
    "YYYY-MM-DD",
  ];

  const parsed = moment(value, formats, true);
  if (parsed.isValid()) {
    return parsed.format("D MMM YYYY h:mm a");
  }

  const fallback = moment(value);
  if (!fallback.isValid()) return "—";
  return fallback.format("D MMM YYYY h:mm a");
}

function getHistoryActionPillClass(action) {
  const normalized = String(action || "").toLowerCase();

  if (normalized.includes("reversal") || normalized.includes("reversed")) {
    return "invoice-edit-pill--reversal";
  }
  if (normalized.includes("refund") || normalized.includes("reverse")) {
    return "invoice-edit-pill--reversal";
  }
  if (normalized.includes("discount")) return "invoice-edit-pill--discount";
  if (normalized.includes("payment")) return "invoice-edit-pill--payment";
  if (normalized.includes("created")) return "invoice-edit-pill--created";
  if (normalized.includes("sent")) return "invoice-edit-pill--sent";
  if (normalized.includes("edited")) return "invoice-edit-pill--edited";
  if (normalized.includes("note")) return "invoice-edit-pill--note";

  return "invoice-edit-pill--neutral";
}

const NOTES_PER_PAGE = 10;

function InvoiceEditHistoryNotesPanel({
  title = "History and notes",
  emptyText = "No history recorded for this invoice yet.",
  historyEntries = [],
  onAddNote = null,
  addingNote = false,
  loading = false,
  followupConfig = null,
  onVisible = null,
}) {
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState("history");
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupPendingCount, setFollowupPendingCount] = useState(0);
  const [followupsOpened, setFollowupsOpened] = useState(false);
  const [followupUsers, setFollowupUsers] = useState(
    followupConfig?.users || [],
  );
  const [followupCatalogLoading, setFollowupCatalogLoading] = useState(false);
  const [followupCatalogError, setFollowupCatalogError] = useState("");
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rootRef = useRef(null);
  const requestedRef = useRef(false);

  const entries = useMemo(
    () =>
      [...historyEntries].sort(
        (left, right) =>
          new Date(right.date).getTime() - new Date(left.date).getTime(),
      ),
    [historyEntries],
  );

  const totalPages = Math.max(1, Math.ceil(entries.length / NOTES_PER_PAGE));

  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * NOTES_PER_PAGE;
    return entries.slice(startIndex, startIndex + NOTES_PER_PAGE);
  }, [entries, currentPage]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [entries.length]);

  useEffect(() => {
    if (!onVisible || requestedRef.current) return undefined;
    const node = rootRef.current;
    if (!node) return undefined;

    const request = () => {
      if (requestedRef.current) return;
      requestedRef.current = true;
      onVisible();
    };

    if (typeof IntersectionObserver === "undefined") {
      request();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          request();
          observer.disconnect();
        }
      },
      { rootMargin: "120px", threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onVisible]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timer = window.setTimeout(() => setSuccessMessage(""), 5000);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  const closeNoteModal = () => {
    if (addingNote) return;
    setShowNoteModal(false);
    setNoteDraft("");
  };

  const openNoteModal = () => {
    setExpanded(true);
    setNoteDraft("");
    setShowNoteModal(true);
  };

  const handleAddNote = async () => {
    const text = noteDraft.trim();
    if (!text || !onAddNote) return;

    const saved = await onAddNote(text);
    if (saved) {
      setNoteDraft("");
      setShowNoteModal(false);
      setCurrentPage(1);
      setSuccessMessage("Note added successfully.");
    }
  };

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages],
  );

  const showingFrom =
    entries.length === 0 ? 0 : (currentPage - 1) * NOTES_PER_PAGE + 1;
  const showingTo = Math.min(currentPage * NOTES_PER_PAGE, entries.length);

  const canManageFollowups = useMemo(() => {
    if (!followupConfig?.contextType) return false;
    try {
      const permissions = JSON.parse(JSON.stringify(getPermissions()) || "{}");
      return hasInvoiceFollowupPermission(
        followupConfig.contextType,
        permissions,
      );
    } catch {
      return false;
    }
  }, [followupConfig?.contextType]);

  useEffect(() => {
    setFollowupPendingCount(Number(followupConfig?.pendingCount) || 0);
    setFollowupsOpened(false);
  }, [followupConfig?.financeInvoiceId]);

  useEffect(() => {
    setFollowupPendingCount(Number(followupConfig?.pendingCount) || 0);
  }, [followupConfig?.pendingCount]);

  useEffect(() => {
    if (Array.isArray(followupConfig?.users) && followupConfig.users.length) {
      setFollowupUsers(followupConfig.users);
    }
  }, [followupConfig?.users]);

  const ensureFollowupUsers = async () => {
    if (followupUsers.length) return true;
    setFollowupCatalogLoading(true);
    setFollowupCatalogError("");
    try {
      const catalog = await fetchInvoiceCatalog();
      const users = applyInvoiceCatalog(catalog).followupUsers;
      setFollowupUsers(users);
      return true;
    } catch (error) {
      setFollowupCatalogError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load follow-up users.",
      );
      return false;
    } finally {
      setFollowupCatalogLoading(false);
    }
  };

  const handleOpenFollowupsTab = () => {
    setFollowupsOpened(true);
    setActiveTab("followups");
  };

  const handleOpenFollowupModal = async () => {
    const ready = await ensureFollowupUsers();
    if (ready) setShowFollowupModal(true);
  };

  return (
    <>
      <section
        ref={rootRef}
        className={`invoice-edit-section invoice-edit-history${
          expanded ? " invoice-edit-section--expanded" : ""
        }`}
      >
        <div className="invoice-edit-section__header">
          <button
            type="button"
            className="invoice-edit-section__toggle"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
          >
            {expanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
            <h3 className="invoice-edit-section__title mb-0">
              {title}
            </h3>
            <span className="invoice-edit-count-badge">{entries.length}</span>
          </button>

          <div className="d-flex flex-wrap gap-2 align-items-center">
            {followupConfig?.financeInvoiceId && canManageFollowups ? (
              <InvoiceFollowupButton
                label={followupCatalogLoading ? "Loading…" : "Add follow-up"}
                onClick={handleOpenFollowupModal}
                disabled={followupCatalogLoading}
              />
            ) : null}
            {onAddNote && activeTab === "history" ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary invoice-edit-section__action"
                onClick={openNoteModal}
                disabled={addingNote || loading}
              >
                Add note
              </button>
            ) : null}
          </div>
        </div>

        {expanded && followupConfig?.financeInvoiceId ? (
          <div
            className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50/80 px-2 py-1.5"
            style={{ display: "flex" }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold ${
                activeTab === "history"
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                  : "text-slate-500"
              }`}
              style={{ border: "none" }}
            >
              History & notes
              <span className="ml-1 text-[10px]">({entries.length})</span>
            </button>
            <button
              type="button"
              onClick={handleOpenFollowupsTab}
              className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold ${
                activeTab === "followups"
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                  : "text-slate-500"
              }`}
              style={{ border: "none" }}
            >
              Follow-ups
              <span className="ml-1 text-[10px]">({followupPendingCount})</span>
            </button>
          </div>
        ) : null}
        {followupCatalogError ? (
          <div className="px-3 py-2 text-[12px] text-red-700">
            {followupCatalogError}{" "}
            <button
              type="button"
              className="font-semibold underline"
              onClick={handleOpenFollowupModal}
            >
              Retry
            </button>
          </div>
        ) : null}

        {expanded && (
          <div className="invoice-edit-section__body">
            {followupsOpened && followupConfig?.financeInvoiceId ? (
              <div
                className="px-1 py-2"
                hidden={activeTab !== "followups"}
              >
                <InvoiceFollowupsTabPanel
                  active={activeTab === "followups"}
                  financeInvoiceId={followupConfig.financeInvoiceId}
                  contextType={followupConfig.contextType}
                  clientId={followupConfig.clientId}
                  providerId={followupConfig.providerId}
                  commissionPlanId={followupConfig.commissionPlanId}
                  applicationId={followupConfig.applicationId}
                  applicationCourseId={followupConfig.applicationCourseId}
                  users={followupUsers}
                  refreshKey={followupConfig.refreshKey || 0}
                  onFollowupsLoaded={(list) => {
                    setFollowupPendingCount(
                      (list || []).filter((r) => r.status === "pending").length,
                    );
                    followupConfig.onFollowupsChanged?.(list);
                  }}
                />
              </div>
            ) : null}
            <div hidden={activeTab !== "history"}>
              <>
            {successMessage && (
              <div className="invoice-edit-history__success-alert" role="status">
                <span>{successMessage}</span>
                <button
                  type="button"
                  className="invoice-edit-history__success-close"
                  onClick={() => setSuccessMessage("")}
                  aria-label="Dismiss message"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            )}

            {loading ? (
              <div className="invoice-edit-history__loading">
                <Loader />
                <span>Loading history…</span>
              </div>
            ) : entries.length > 0 ? (
              <div className="invoice-edit-table-wrap invoice-edit-table-wrap--flush">
                <table className="invoice-edit-table invoice-edit-table--history">
                  <thead>
                    <tr>
                      <th>
                        <span className="invoice-edit-table__sort-label">
                          Date
                        </span>
                      </th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="invoice-edit-history__date">
                          {formatHistoryDate(entry.date)}
                        </td>
                        <td className="invoice-edit-table__muted">
                          {entry.user || "—"}
                        </td>
                        <td>
                          <span
                            className={`invoice-edit-pill ${getHistoryActionPillClass(entry.action)}`}
                          >
                            {entry.action || "—"}
                          </span>
                        </td>
                        <td className="invoice-edit-history__detail">
                          {entry.detail || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="invoice-edit-empty invoice-edit-empty--section">
                <FaInbox
                  className="invoice-edit-empty__icon"
                  aria-hidden="true"
                />
                <p className="invoice-edit-empty__text">
                  {emptyText}
                </p>
              </div>
            )}

            {activeTab === "history" && entries.length > NOTES_PER_PAGE ? (
              <div className="invoice-edit-history__pagination">
                <span className="invoice-edit-history__pagination-info">
                  Showing {showingFrom}–{showingTo} of {entries.length} notes
                </span>
                <div className="invoice-edit-history__pagination-pages">
                  <button
                    type="button"
                    className="invoice-edit-history__pagination-btn"
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  {pageNumbers.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`invoice-edit-history__pagination-btn${
                        pageNumber === currentPage ? " is-active" : ""
                      }`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="invoice-edit-history__pagination-btn"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
              </>
            </div>
          </div>
        )}
      </section>

      {followupConfig?.financeInvoiceId ? (
        <InvoiceFollowupModal
          show={showFollowupModal}
          contextType={followupConfig.contextType}
          financeInvoiceId={followupConfig.financeInvoiceId}
          commissionPlanId={followupConfig.commissionPlanId}
          clientId={followupConfig.clientId}
          providerId={followupConfig.providerId}
          applicationId={followupConfig.applicationId}
          applicationCourseId={followupConfig.applicationCourseId}
          users={followupUsers}
          onClose={() => setShowFollowupModal(false)}
          onSuccess={() => {
            setShowFollowupModal(false);
            setFollowupPendingCount((count) => count + 1);
            followupConfig.onFollowupAdded?.();
          }}
        />
      ) : null}

      {showNoteModal ? (
        <>
          <div
            className="invoice-edit-note-modal__backdrop"
            onClick={closeNoteModal}
            aria-hidden="true"
          />
          <div
            className="invoice-edit-note-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-edit-note-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="invoice-edit-note-modal__header">
              <h4
                id="invoice-edit-note-modal-title"
                className="invoice-edit-note-modal__title mb-0"
              >
                Add note
              </h4>
              <button
                type="button"
                className="invoice-edit-note-modal__close"
                onClick={closeNoteModal}
                disabled={addingNote}
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div className="invoice-edit-note-modal__body">
              <label
                className="invoice-edit-note-modal__label"
                htmlFor="invoiceEditNote"
              >
                Note
              </label>
              <textarea
                id="invoiceEditNote"
                className="form-control invoice-edit-note-modal__textarea"
                rows={5}
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                placeholder="Add a note about this invoice…"
                disabled={addingNote}
                autoFocus
              />
            </div>

            <div className="invoice-edit-note-modal__footer">
              <button
                type="button"
                className="invoice-edit-note-modal__btn invoice-edit-note-modal__btn--cancel"
                onClick={closeNoteModal}
                disabled={addingNote}
              >
                Cancel
              </button>
              <button
                type="button"
                className="invoice-edit-note-modal__btn invoice-edit-note-modal__btn--save"
                onClick={handleAddNote}
                disabled={addingNote || !noteDraft.trim()}
              >
                {addingNote ? "Saving…" : "Save note"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

export default InvoiceEditHistoryNotesPanel;
